import { PaymentErrorCodes } from '@app/types/monolith/PaymentErrorCodes';
import { AnalyticsPageType } from '@redbubble/next-analytics/gtm';
import {
  Stripe,
  StripeElements,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { getPaymentErrorMessage } from '../paymentErrorLookup';
import {
  ErrorMessage,
  ExpressPayment,
  ExpressPaymentType,
  PageSourceType,
  RbPaymentMethods,
  StripeExpressAnalyticsPageType,
} from '../types';
import { PayRequestBody, PayResult } from '../usePay';
import {
  convertStripeResponseToPayRequestBody,
  createPaymentRedirectUrl,
} from '../utils';

const EXPRESS_CHARGE_SOURCES: Record<
  StripeExpressAnalyticsPageType,
  Record<ExpressPaymentType, string>
> = {
  [AnalyticsPageType.Cart]: {
    [ExpressPayment.APPLE_PAY]: 'APPLE_PAY_EXPRESS',
    [ExpressPayment.GOOGLE_PAY]: 'GOOGLE_PAY_EXPRESS',
  },
  [AnalyticsPageType.ProductDetailsPage]: {
    [ExpressPayment.APPLE_PAY]: 'APPLE_PAY_EXPRESS_CART_TRAY',
    [ExpressPayment.GOOGLE_PAY]: 'GOOGLE_PAY_EXPRESS_CART_TRAY',
  },
  [AnalyticsPageType.Checkout]: {
    [ExpressPayment.APPLE_PAY]: 'APPLE_PAY_EXPRESS_CHECKOUT',
    [ExpressPayment.GOOGLE_PAY]: 'GOOGLE_PAY_EXPRESS_CHECKOUT',
  },
};

export const handleConfirm = async (
  analyticsPageType: StripeExpressAnalyticsPageType,
  pageSource: PageSourceType,
  event: StripeExpressCheckoutElementConfirmEvent,
  postPay: (req: PayRequestBody) => Promise<PayResult>,
  onErrorCallback: (message: ErrorMessage) => void,
  currency: string,
  stripe: Stripe,
  elements: StripeElements,
  rbEmail: string | null,
  consent?: boolean
) => {
  // https://stripe.com/docs/js/elements_object/express_checkout_element_confirm_event
  const req = convertStripeResponseToPayRequestBody(
    event,
    RbPaymentMethods.Card,
    pageSource,
    rbEmail,
    consent
  );

  const result = await postPay(req);
  const { client_secret: clientSecret } = result.data || {};

  if (result.errors || !clientSecret) {
    // we only show the first server error message
    const error = (result.errors || [])[0] || {
      code: PaymentErrorCodes.SERVER_ERROR,
    };
    const errorMessage = getPaymentErrorMessage(error);
    onErrorCallback(errorMessage);
    event.paymentFailed({ reason: 'fail' });
    return;
  }

  const chargeSource =
    EXPRESS_CHARGE_SOURCES[analyticsPageType][event.expressPaymentType];
  const redirectUrl = createPaymentRedirectUrl({
    chargeSource,
    currency,
  });

  // https://stripe.com/docs/js/payment_intents/confirm_payment
  // NOTE: we are forcing a redirect regardless of payment method, so
  //       Stripe appends "payment_intent" to the return URL, which
  //       satisfies what our verify intent endpoint expects
  await stripe.confirmPayment({
    elements,
    clientSecret,
    confirmParams: {
      return_url: redirectUrl.href,
    },
    redirect: 'always',
  });

  // customers are always redirected on success
  // so we will only get here if payment failed
  onErrorCallback(
    getPaymentErrorMessage({
      code: PaymentErrorCodes.CHARGE_FAILED,
    })
  );
};
